import React, { useState, lazy } from 'react';
import PageHeader from '../components/layout/PageHeader';
import Gallery from '../components/ui/Gallery';
import Tabs from '../components/ui/Tabs';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import OptimizedImage from '../components/ui/OptimizedImage';
import { ArrowRightIcon, CheckIcon, HeartIcon } from 'lucide-react';
interface FacilityImage {
  src: string;
  alt: string;
  caption?: string;
}
interface Facility {
  id: string;
  name: string;
  description: string;
  features: string[];
  images: FacilityImage[];
}
const Campus = () => {
  const [currentTourSpot, setCurrentTourSpot] = useState<number>(0);
  const tourSpots = [{
    title: 'Main Administration Building',
    description: 'The central hub of school operations, housing administrative offices, staff rooms, and meeting spaces.',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/de3EQh38fKgVchW9xJS4du/WhatsApp_Image_2025-10-24_at_10.01.26.jpg'
  }, {
    title: 'Campus Overview',
    description: 'Aerial view of our beautiful campus showing the academic buildings, green spaces, and well-maintained grounds that create an ideal learning environment.',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/guWk5XsDGYmXXcoEtCyUpd/WhatsApp_Image_2025-10-24_at_11.30.12_%281%29.jpg'
  }, {
    title: 'Academic Buildings',
    description: 'Our spacious academic buildings with covered walkways and landscaped gardens provide a conducive environment for learning and student activities.',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/jdfoyTCXLviMyjmi5MDX1X/WhatsApp_Image_2025-10-24_at_11.30.13.jpg'
  }, {
    title: 'Classroom Blocks',
    description: 'Modern classroom blocks with covered walkways connecting different sections of the school, designed for easy access and student comfort.',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/8LoteQK39b5zMuJh2pbsp8/WhatsApp_Image_2025-10-24_at_11.30.11.jpg'
  }, {
    title: 'School Grounds',
    description: 'Expansive school grounds with well-maintained pathways, green lawns, and landscaped areas that provide spaces for recreation and outdoor activities.',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/mMF9fzVCb1rew9Z9Ba8RBR/WhatsApp_Image_2025-10-24_at_11.30.12.jpg'
  }, {
    title: 'Boarding Facilities',
    description: 'Our two-story boarding house provides comfortable accommodation for students with balconies, common areas, and modern amenities.',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/4jLMnmKXSAWwavqGR8zdhM/WhatsApp_Image_2025-10-24_at_11.30.13_%281%29.jpg'
  }, {
    title: 'Principal Quarter',
    description: 'The official residence of the school principal, located within the school premises for easy oversight and management.',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/oq7mq9TcPHpj2uqzCbrBtL/WhatsApp_Image_2025-10-24_at_10.01.15.jpg'
  }, {
    title: 'Junior School Section',
    description: 'Dedicated classrooms for junior students with modern learning environments and innovative teaching methods including PSI/PMI pedagogy.',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/b9sNC2W3z8KYjzQztqrzUk/WhatsApp_Image_2025-10-24_at_10.01.23_%281%29.jpg'
  }, {
    title: 'Morning Assembly Ground',
    description: 'Spacious assembly ground where students gather for morning assembly, announcements, and school-wide activities.',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/3DHAcubmTqmT13YpTTNQmr/WhatsApp_Image_2025-10-24_at_10.01.24.jpg'
  }, {
    title: 'Chemistry Laboratory',
    description: 'Well-equipped chemistry laboratory where students conduct practical experiments and develop scientific skills.',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/5gKYWryVTvwM3jPw4Bpnok/WhatsApp_Image_2025-10-24_at_10.01.16.jpg'
  }, {
    title: 'Computer Laboratory',
    description: 'Modern ICT laboratory featuring computer workstations and educational software for practical computer science lessons.',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/4bkQakMXE7oCJhU6DHfZ4v/WhatsApp_Image_2025-10-24_at_10.01.16_%281%29.jpg'
  }];
  const facilities = [{
    id: 'classrooms',
    name: 'Classrooms',
    description: 'Our spacious and well-ventilated classrooms are designed to create an optimal learning environment. Each classroom is equipped with modern teaching aids to facilitate effective learning. We implement the PSI/PMI pedagogy developed by the New Jersey Center for Teaching and Learning, U.S.A., adopted by the Ogun State Government.',
    features: ['Smart boards in all classrooms', 'Ergonomic furniture for student comfort', 'Proper lighting and ventilation', 'Audio-visual equipment', 'Wi-Fi connectivity', 'PSI/PMI teaching methodology'],
    images: [{
      src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/8LoteQK39b5zMuJh2pbsp8/WhatsApp_Image_2025-10-24_at_11.30.11.jpg',
      alt: 'Classroom blocks with covered walkway',
      caption: 'Modern classroom blocks with covered walkways'
    }, {
      src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/b9sNC2W3z8KYjzQztqrzUk/WhatsApp_Image_2025-10-24_at_10.01.23_%281%29.jpg',
      alt: 'Junior school classroom',
      caption: 'Active learning in our junior school section'
    }, {
      src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/qfRUYQw3ECRPT1ECfn8AxJ/WhatsApp_Image_2025-10-24_at_10.01.23.jpg',
      alt: 'PSI/PMI class in action',
      caption: 'PSI/PMI pedagogy in practice in computer lab'
    }]
  }, {
    id: 'laboratories',
    name: 'Science Laboratories',
    description: 'Our school boasts modern, well-equipped laboratories for Physics, Chemistry, Biology, and Computer Science. These facilities provide students with hands-on experience and practical knowledge in scientific concepts.',
    features: ['Separate labs for Physics, Chemistry, and Biology', 'Modern scientific equipment and apparatus', 'Safety equipment and protocols', 'Qualified lab assistants', 'Regular maintenance and updates'],
    images: [{
      src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/5gKYWryVTvwM3jPw4Bpnok/WhatsApp_Image_2025-10-24_at_10.01.16.jpg',
      alt: 'Chemistry laboratory',
      caption: 'Students conducting chemistry experiments in our lab'
    }, {
      src: 'https://images.unsplash.com/photo-1581093450021-4a7360e9a6b5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1740&q=80',
      alt: 'Physics laboratory',
      caption: 'Physics laboratory with experimental setups'
    }, {
      src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/4bkQakMXE7oCJhU6DHfZ4v/WhatsApp_Image_2025-10-24_at_10.01.16_%281%29.jpg',
      alt: 'Computer laboratory',
      caption: 'Modern computer laboratory with latest equipment'
    }]
  }, {
    id: 'library',
    name: 'Library',
    description: 'Our library is a quiet haven for study and research, housing a vast collection of books, periodicals, and digital resources. The library is designed to foster a love for reading and independent learning.',
    features: ['Extensive collection of books across subjects', 'Digital catalog and borrowing system', 'Quiet study areas and discussion rooms', 'Internet-enabled computer stations', 'Professional librarians to assist students'],
    images: [{
      src: 'https://images.unsplash.com/photo-1577896852618-3b98bb03f47b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1740&q=80',
      alt: 'School library',
      caption: 'Main reading area of the school library'
    }, {
      src: 'https://images.unsplash.com/photo-1521587760476-6c12a4b040da?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1770&q=80',
      alt: 'Library bookshelves',
      caption: 'Extensive collection of academic and literary books'
    }, {
      src: 'https://images.unsplash.com/photo-1541829070764-84a7d30dd3f3?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1769&q=80',
      alt: 'Study area',
      caption: 'Quiet study area for individual research and reading'
    }]
  }, {
    id: 'sports',
    name: 'Sports Facilities',
    description: 'Our comprehensive sports facilities support the physical development of students and foster a spirit of teamwork and healthy competition. We offer a wide range of sporting activities to cater to diverse interests.',
    features: ['Standard football field with running track', 'Basketball and volleyball courts', 'Indoor sports hall', 'Swimming pool', 'Changing rooms and equipment storage'],
    images: [{
      src: 'https://images.unsplash.com/photo-1526232761682-d26e03ac148e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1829&q=80',
      alt: 'Football field',
      caption: 'Standard football field with running track'
    }, {
      src: 'https://images.unsplash.com/photo-1505666287802-931dc83a5dc7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1742&q=80',
      alt: 'Basketball court',
      caption: 'Outdoor basketball court'
    }, {
      src: 'https://images.unsplash.com/photo-1575429198097-0414ec08e8cd?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1770&q=80',
      alt: 'Swimming pool',
      caption: 'School swimming pool for training and competitions'
    }]
  }, {
    id: 'boarding',
    name: 'Boarding Facilities',
    description: 'Our boarding houses provide a comfortable and secure home away from home for students. The facilities are designed to foster independence, responsibility, and a sense of community among boarding students.',
    features: ['Separate dormitories for boys and girls', 'Comfortable bunk beds with storage space', 'Modern bathroom facilities', 'Common rooms with recreational facilities', '24-hour security and supervision'],
    images: [{
      src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/4jLMnmKXSAWwavqGR8zdhM/WhatsApp_Image_2025-10-24_at_11.30.13_%281%29.jpg',
      alt: 'Two-story boarding house',
      caption: 'Modern boarding house with balconies and common areas'
    }, {
      src: 'https://images.unsplash.com/photo-1588072432836-e10032774350?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1744&q=80',
      alt: 'Boarding house dormitory',
      caption: "Boys' dormitory with bunk beds"
    }, {
      src: 'https://images.unsplash.com/photo-1555854877-bab0e564b8d5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1769&q=80',
      alt: 'Common room',
      caption: 'Boarding house common room for relaxation'
    }]
  }, {
    id: 'dining',
    name: 'Dining Hall',
    description: 'Our spacious dining hall serves nutritious and balanced meals to students. The facility is maintained with the highest standards of hygiene and food safety.',
    features: ['Capacity to seat all boarding students', 'Modern kitchen with professional catering staff', 'Balanced and nutritious meals', 'Special dietary accommodations', 'Regular health and safety inspections'],
    images: [{
      src: 'https://images.unsplash.com/photo-1544427920-c49ccfb85579?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1771&q=80',
      alt: 'Dining hall',
      caption: 'Spacious dining hall with tables and chairs'
    }, {
      src: 'https://images.unsplash.com/photo-1466637574441-749b8f19452f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1780&q=80',
      alt: 'Kitchen',
      caption: 'Modern kitchen with professional equipment'
    }, {
      src: 'https://images.unsplash.com/photo-1621274403997-37aace184f49?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1760&q=80',
      alt: 'Food serving',
      caption: 'Nutritious meals served to students'
    }]
  }, {
    id: 'health',
    title: 'Health & Wellness Center',
    description: 'Our modern school clinic provides comprehensive healthcare services to students and staff, ensuring the well-being of our school community.',
    icon: <HeartIcon className="h-8 w-8 text-blue-700" data-id="element-2678" />,
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/69pV2ekxejVNy9Zw2uynoL/WhatsApp_Image_2025-10-24_at_10.01.14.jpg',
    features: ['On-campus medical clinic', 'Qualified healthcare professionals', 'Emergency first aid services', 'Regular health screenings', 'Partnerships with nearby hospitals']
  }];
  const campusGalleryImages = [{
    src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/jdfoyTCXLviMyjmi5MDX1X/WhatsApp_Image_2025-10-24_at_11.30.13.jpg',
    alt: 'Academic buildings with landscaped gardens',
    caption: 'Academic buildings with beautiful landscaped gardens'
  }, {
    src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/guWk5XsDGYmXXcoEtCyUpd/WhatsApp_Image_2025-10-24_at_11.30.12_%281%29.jpg',
    alt: 'Campus aerial view',
    caption: 'Aerial view of our beautiful campus'
  }, {
    src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/mMF9fzVCb1rew9Z9Ba8RBR/WhatsApp_Image_2025-10-24_at_11.30.12.jpg',
    alt: 'School grounds with pathways',
    caption: 'Well-maintained pathways and green lawns'
  }, {
    src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/8LoteQK39b5zMuJh2pbsp8/WhatsApp_Image_2025-10-24_at_11.30.11.jpg',
    alt: 'Classroom blocks',
    caption: 'Classroom blocks with covered walkways'
  }, {
    src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/4jLMnmKXSAWwavqGR8zdhM/WhatsApp_Image_2025-10-24_at_11.30.13_%281%29.jpg',
    alt: 'Boarding house',
    caption: 'Two-story boarding house with balconies'
  }, {
    src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/de3EQh38fKgVchW9xJS4du/WhatsApp_Image_2025-10-24_at_10.01.26.jpg',
    alt: 'Administrative Block',
    caption: 'Main administrative building'
  }, {
    src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/3DHAcubmTqmT13YpTTNQmr/WhatsApp_Image_2025-10-24_at_10.01.24.jpg',
    alt: 'Morning Assembly',
    caption: 'Students at morning assembly'
  }, {
    src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/oq7mq9TcPHpj2uqzCbrBtL/WhatsApp_Image_2025-10-24_at_10.01.15.jpg',
    alt: 'Principal Quarter',
    caption: 'Principal Quarter building'
  }, {
    src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/b9sNC2W3z8KYjzQztqrzUk/WhatsApp_Image_2025-10-24_at_10.01.23_%281%29.jpg',
    alt: 'Junior School Classroom',
    caption: 'Junior school section in session'
  }, {
    src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/qfRUYQw3ECRPT1ECfn8AxJ/WhatsApp_Image_2025-10-24_at_10.01.23.jpg',
    alt: 'PSI/PMI Class',
    caption: 'Innovative teaching methods in practice'
  }, {
    src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/5gKYWryVTvwM3jPw4Bpnok/WhatsApp_Image_2025-10-24_at_10.01.16.jpg',
    alt: 'Chemistry laboratory',
    caption: 'Chemistry laboratory practical session'
  }, {
    src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/4bkQakMXE7oCJhU6DHfZ4v/WhatsApp_Image_2025-10-24_at_10.01.16_%281%29.jpg',
    alt: 'Computer laboratory',
    caption: 'ICT laboratory with modern computers'
  }];
  const facilitiesHighlights = [{
    title: 'Modern Classroom Blocks',
    description: 'Spacious and well-ventilated classrooms designed for optimal learning',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/hgRGwnKLZHVDTRdgag3Y5F/WhatsApp_Image_2025-10-24_at_10.01.11.jpg'
  }, {
    title: 'Engaged Learning Environment',
    description: 'Students actively participating in lessons with dedicated teachers',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/fhbXs7UNN6d2HJRiEebjre/WhatsApp_Image_2025-10-24_at_10.01.20.jpg'
  }, {
    title: 'Computer Laboratory',
    description: 'Modern computer lab for digital literacy and technology education',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/3GFAKQjjuFe5vR4bv8XoiJ/WhatsApp_Image_2025-10-24_at_10.01.21_%281%29.jpg'
  }, {
    title: 'Technology-Enhanced Learning',
    description: 'Teachers using modern technology to deliver engaging lessons',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/26CHK6sEK66nfVUNdvbwf4/WhatsApp_Image_2025-10-24_at_10.01.20_%282%29.jpg'
  }, {
    title: 'Assembly Hall',
    description: 'Large multipurpose hall for assemblies, events, and examinations',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/3uhcY5Mx6ch6FMKTi9n6s3/WhatsApp_Image_2025-10-24_at_10.01.12.jpg'
  }, {
    title: 'Science Laboratories',
    description: 'Well-equipped laboratories for hands-on scientific learning and experiments',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/ez9ThFy3DkQ6XwMpYtSRNQ/WhatsApp_Image_2025-10-24_at_10.01.16.jpg'
  }];
  return <div className="min-h-screen bg-white" data-id="element-2679">
      <PageHeader title="Our Campus" description="Explore the facilities and environment that make Isonyin Grammar School an ideal place for learning and growth." backgroundImage="https://uploadthingy.s3.us-west-1.amazonaws.com/jdfoyTCXLviMyjmi5MDX1X/WhatsApp_Image_2025-10-24_at_11.30.13.jpg" breadcrumbs={[{
      label: 'Home',
      path: '/'
    }, {
      label: 'Campus',
      path: '/campus'
    }]} data-id="element-2680" />
      <div className="container mx-auto px-4 py-12" data-id="element-2681">
        {/* Campus Overview */}
        <section className="mb-16" data-id="element-2682">
          <div className="text-center mb-10" data-id="element-2683">
            <h2 className="text-3xl font-bold text-blue-900 mb-3" data-id="element-2684">
              Welcome to Our Campus
            </h2>
            <div className="w-24 h-1 bg-yellow-500 mx-auto" data-id="element-2685"></div>
            <p className="mt-4 text-gray-600 max-w-2xl mx-auto" data-id="element-2686">
              Isonyin Grammar School's campus spans over 25 acres of beautifully
              landscaped grounds, providing an ideal environment for academic
              excellence, personal growth, and extracurricular activities.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center" data-id="element-2687">
            <div data-id="element-2688">
              <p className="text-gray-700 mb-6 leading-relaxed" data-id="element-2689">
                Our campus is designed to provide students with a conducive
                environment for learning, recreation, and personal development.
                The school buildings blend modern architecture with traditional
                elements, creating a unique and inspiring atmosphere.
              </p>
              <p className="text-gray-700 mb-6 leading-relaxed" data-id="element-2690">
                The campus is divided into different zones including academic
                blocks, administrative buildings, sports facilities, and
                boarding houses. Each zone is designed to meet specific needs
                while maintaining a cohesive campus feel.
              </p>
              <p className="text-gray-700 leading-relaxed" data-id="element-2691">
                Safety and security are paramount at Isonyin Grammar School. The
                entire campus is secured with perimeter fencing, security
                personnel, and CCTV surveillance to ensure the wellbeing of all
                students and staff.
              </p>
              <div className="mt-8 grid grid-cols-2 gap-4" data-id="element-2692">
                <div className="bg-blue-50 p-4 rounded-lg" data-id="element-2693">
                  <div className="text-blue-900 font-bold mb-2" data-id="element-2694">25+ Acres</div>
                  <p className="text-sm text-gray-600" data-id="element-2695">Total campus area</p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg" data-id="element-2696">
                  <div className="text-blue-900 font-bold mb-2" data-id="element-2697">1956</div>
                  <p className="text-sm text-gray-600" data-id="element-2698">Year established</p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg" data-id="element-2699">
                  <div className="text-blue-900 font-bold mb-2" data-id="element-2700">
                    15 Buildings
                  </div>
                  <p className="text-sm text-gray-600" data-id="element-2701">
                    Academic & residential
                  </p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg" data-id="element-2702">
                  <div className="text-blue-900 font-bold mb-2" data-id="element-2703">500+</div>
                  <p className="text-sm text-gray-600" data-id="element-2704">Boarding capacity</p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4" data-id="element-2705">
              <div data-id="element-2706">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/guWk5XsDGYmXXcoEtCyUpd/WhatsApp_Image_2025-10-24_at_11.30.12_%281%29.jpg" alt="Campus aerial view" className="w-full h-48 object-cover rounded-lg mb-4" data-id="element-2707" />
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/5gKYWryVTvwM3jPw4Bpnok/WhatsApp_Image_2025-10-24_at_10.01.16.jpg" alt="Chemistry laboratory" className="w-full h-40 object-cover rounded-lg" data-id="element-2708" />
              </div>
              <div className="mt-6" data-id="element-2709">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/mMF9fzVCb1rew9Z9Ba8RBR/WhatsApp_Image_2025-10-24_at_11.30.12.jpg" alt="School grounds" className="w-full h-40 object-cover rounded-lg mb-4" data-id="element-2710" />
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/4jLMnmKXSAWwavqGR8zdhM/WhatsApp_Image_2025-10-24_at_11.30.13_%281%29.jpg" alt="Boarding house" className="w-full h-48 object-cover rounded-lg" data-id="element-2711" />
              </div>
            </div>
          </div>
        </section>

        {/* Facilities Highlights Section */}
        <section className="mb-16" data-id="element-2712">
          <div className="text-center mb-10" data-id="element-2713">
            <h2 className="text-3xl font-bold text-blue-900 mb-3" data-id="element-2714">
              Facilities Highlights
            </h2>
            <div className="w-24 h-1 bg-yellow-500 mx-auto" data-id="element-2715"></div>
            <p className="mt-4 text-gray-600 max-w-2xl mx-auto" data-id="element-2716">
              Explore our modern facilities designed to provide the best
              learning environment for academic excellence and personal growth
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8" data-id="element-2717">
            {facilitiesHighlights.map((facility, index) => <Card key={index} hover padding="none" className="overflow-hidden" data-id="element-2718">
                <OptimizedImage src={facility.image} alt={facility.title} className="w-full" aspectRatio="16/9" data-id="element-2719" />
                <div className="p-5" data-id="element-2720">
                  <h3 className="font-bold text-blue-900 mb-2 text-lg" data-id="element-2721">
                    {facility.title}
                  </h3>
                  <p className="text-gray-700 text-sm leading-relaxed" data-id="element-2722">
                    {facility.description}
                  </p>
                </div>
              </Card>)}
          </div>

          <div className="text-center" data-id="element-2723">
            <p className="text-gray-600 mb-4" data-id="element-2724">
              Discover more about our comprehensive facilities and how they
              support student success
            </p>
            <Button href="#facilities" variant="primary" icon={<ArrowRightIcon data-id="element-2726" />} iconPosition="right" data-id="element-2725">
              View Detailed Facilities
            </Button>
          </div>
        </section>

        {/* Virtual Tour */}
        <section className="mb-16" id="tour" data-id="element-2727">
          <div className="text-center mb-10" data-id="element-2728">
            <h2 className="text-3xl font-bold text-blue-900 mb-3" data-id="element-2729">
              Virtual Tour
            </h2>
            <div className="w-24 h-1 bg-yellow-500 mx-auto" data-id="element-2730"></div>
            <p className="mt-4 text-gray-600 max-w-2xl mx-auto" data-id="element-2731">
              Take a virtual tour of our campus and explore the facilities that
              make Isonyin Grammar School an excellent environment for learning
              and personal development.
            </p>
          </div>
          <div className="bg-blue-50 rounded-lg overflow-hidden" data-id="element-2732">
            <div className="relative h-96" data-id="element-2733">
              <img src={tourSpots[currentTourSpot].image} alt={tourSpots[currentTourSpot].title} className="w-full h-full object-cover transition-opacity duration-500" data-id="element-2734" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end" data-id="element-2735">
                <div className="p-6 text-white" data-id="element-2736">
                  <h3 className="text-xl font-bold mb-2" data-id="element-2737">
                    {tourSpots[currentTourSpot].title}
                  </h3>
                  <p data-id="element-2738">{tourSpots[currentTourSpot].description}</p>
                </div>
              </div>
            </div>
            <div className="p-6" data-id="element-2739">
              <div className="flex justify-between items-center mb-4" data-id="element-2740">
                <button onClick={() => setCurrentTourSpot(prev => prev === 0 ? tourSpots.length - 1 : prev - 1)} className="px-4 py-2 bg-blue-900 text-white rounded hover:bg-blue-800 transition-colors" data-id="element-2741">
                  Previous
                </button>
                <div className="text-blue-900 font-medium" data-id="element-2742">
                  {currentTourSpot + 1} / {tourSpots.length}
                </div>
                <button onClick={() => setCurrentTourSpot(prev => prev === tourSpots.length - 1 ? 0 : prev + 1)} className="px-4 py-2 bg-blue-900 text-white rounded hover:bg-blue-800 transition-colors" data-id="element-2743">
                  Next
                </button>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-id="element-2744">
                {tourSpots.map((spot, index) => <div key={index} className={`cursor-pointer rounded overflow-hidden border-2 ${currentTourSpot === index ? 'border-blue-900' : 'border-transparent'}`} onClick={() => setCurrentTourSpot(index)} data-id="element-2745">
                    <img src={spot.image} alt={spot.title} className="w-full h-16 object-cover" data-id="element-2746" />
                  </div>)}
              </div>
            </div>
          </div>
          <div className="mt-8 text-center" data-id="element-2747">
            <p className="text-gray-600 mb-4" data-id="element-2748">
              Want to experience our campus in person? Schedule a visit today!
            </p>
            <Button href="/contact" variant="primary" data-id="element-2749">
              Schedule a Campus Visit
            </Button>
          </div>
        </section>

        {/* Campus Facilities */}
        <section className="mb-16" id="facilities" data-id="element-2750">
          <div className="text-center mb-10" data-id="element-2751">
            <h2 className="text-3xl font-bold text-blue-900 mb-3" data-id="element-2752">
              Our Facilities
            </h2>
            <div className="w-24 h-1 bg-yellow-500 mx-auto" data-id="element-2753"></div>
            <p className="mt-4 text-gray-600 max-w-2xl mx-auto" data-id="element-2754">
              Isonyin Grammar School offers state-of-the-art facilities to
              support academic excellence, extracurricular activities, and the
              overall wellbeing of our students.
            </p>
          </div>
          <Tabs tabs={facilities.map(facility => ({
          id: facility.id,
          label: facility.name,
          content: <div className="py-6" data-id="element-2756">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start" data-id="element-2757">
                    <div data-id="element-2758">
                      <p className="text-gray-700 mb-6 leading-relaxed" data-id="element-2759">
                        {facility.description}
                      </p>
                      <h3 className="text-lg font-bold text-blue-900 mb-4" data-id="element-2760">
                        Key Features
                      </h3>
                      <ul className="space-y-3 mb-6" data-id="element-2761">
                        {facility.features.map((feature, index) => <li key={index} className="flex items-start" data-id="element-2762">
                            <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2" data-id="element-2763">
                              <CheckIcon className="h-4 w-4 text-blue-700" data-id="element-2764" />
                            </div>
                            <span className="text-gray-700" data-id="element-2765">{feature}</span>
                          </li>)}
                      </ul>
                      <Button href={`/campus/${facility.id}`} variant="outline" icon={<ArrowRightIcon data-id="element-2767" />} iconPosition="right" data-id="element-2766">
                        Learn More
                      </Button>
                    </div>
                    <Gallery images={facility.images} columns={3} gap="small" data-id="element-2768" />
                  </div>
                </div>
        }))} variant="boxed" data-id="element-2755" />
        </section>

        {/* Campus Gallery */}
        <section className="mb-16" data-id="element-2769">
          <div className="text-center mb-10" data-id="element-2770">
            <h2 className="text-3xl font-bold text-blue-900 mb-3" data-id="element-2771">
              Campus Gallery
            </h2>
            <div className="w-24 h-1 bg-yellow-500 mx-auto" data-id="element-2772"></div>
            <p className="mt-4 text-gray-600 max-w-2xl mx-auto" data-id="element-2773">
              Explore our beautiful campus through these images showcasing our
              facilities and environment.
            </p>
          </div>
          <Gallery images={campusGalleryImages} columns={4} gap="medium" className="mb-6" data-id="element-2774" />
          <div className="text-center" data-id="element-2775">
            <Button href="/gallery" variant="secondary" icon={<ArrowRightIcon data-id="element-2777" />} iconPosition="right" data-id="element-2776">
              View Full Gallery
            </Button>
          </div>
        </section>

        {/* Campus Map */}
        <section className="mb-16" data-id="element-2778">
          <div className="text-center mb-10" data-id="element-2779">
            <h2 className="text-3xl font-bold text-blue-900 mb-3" data-id="element-2780">
              Campus Map
            </h2>
            <div className="w-24 h-1 bg-yellow-500 mx-auto" data-id="element-2781"></div>
            <p className="mt-4 text-gray-600 max-w-2xl mx-auto" data-id="element-2782">
              Navigate our campus with this interactive map showing the location
              of all major buildings and facilities.
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-lg overflow-hidden" data-id="element-2783">
            <div className="h-[500px] bg-gray-200" data-id="element-2784">
              <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3963.3353732384716!2d3.9115800152411387!3d6.609950195219036!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x103bef3a82404c2d%3A0x3b96e0b998e7055c!2sIjebu%20North%20East%2C%20Ogun%20State%2C%20Nigeria!5e0!3m2!1sen!2sus!4v1652795698562!5m2!1sen!2sus" width="100%" height="100%" style={{
              border: 0
            }} allowFullScreen={true} loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Isonyin Grammar School Campus Map" data-id="element-2785"></iframe>
            </div>
            <div className="p-6" data-id="element-2786">
              <h3 className="text-xl font-bold text-blue-900 mb-4" data-id="element-2787">
                Campus Layout
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2788">
                <div className="flex items-start" data-id="element-2789">
                  <div className="w-4 h-4 rounded-full bg-blue-900 mt-1 mr-2" data-id="element-2790"></div>
                  <div data-id="element-2791">
                    <div className="font-medium text-gray-900" data-id="element-2792">
                      Main Administration Block
                    </div>
                    <p className="text-sm text-gray-600" data-id="element-2793">
                      Principal's office, staff rooms, reception
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-2794">
                  <div className="w-4 h-4 rounded-full bg-green-600 mt-1 mr-2" data-id="element-2795"></div>
                  <div data-id="element-2796">
                    <div className="font-medium text-gray-900" data-id="element-2797">
                      Academic Blocks
                    </div>
                    <p className="text-sm text-gray-600" data-id="element-2798">
                      Classrooms, laboratories, library
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-2799">
                  <div className="w-4 h-4 rounded-full bg-red-600 mt-1 mr-2" data-id="element-2800"></div>
                  <div data-id="element-2801">
                    <div className="font-medium text-gray-900" data-id="element-2802">
                      Sports Facilities
                    </div>
                    <p className="text-sm text-gray-600" data-id="element-2803">
                      Football field, basketball courts, sports hall
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-2804">
                  <div className="w-4 h-4 rounded-full bg-yellow-500 mt-1 mr-2" data-id="element-2805"></div>
                  <div data-id="element-2806">
                    <div className="font-medium text-gray-900" data-id="element-2807">
                      Boarding Houses
                    </div>
                    <p className="text-sm text-gray-600" data-id="element-2808">
                      Boys' and girls' dormitories
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-2809">
                  <div className="w-4 h-4 rounded-full bg-purple-600 mt-1 mr-2" data-id="element-2810"></div>
                  <div data-id="element-2811">
                    <div className="font-medium text-gray-900" data-id="element-2812">Dining Hall</div>
                    <p className="text-sm text-gray-600" data-id="element-2813">
                      Cafeteria and kitchen facilities
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-2814">
                  <div className="w-4 h-4 rounded-full bg-gray-600 mt-1 mr-2" data-id="element-2815"></div>
                  <div data-id="element-2816">
                    <div className="font-medium text-gray-900" data-id="element-2817">
                      Other Facilities
                    </div>
                    <p className="text-sm text-gray-600" data-id="element-2818">
                      Assembly hall, clinic, staff quarters
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-6 bg-blue-50 p-4 rounded-lg" data-id="element-2819">
                <h4 className="font-medium text-blue-900 mb-2" data-id="element-2820">
                  Visiting Hours
                </h4>
                <p className="text-gray-700 text-sm" data-id="element-2821">
                  The campus is open to visitors Monday through Friday from 9:00
                  AM to 3:00 PM. All visitors must report to the security post
                  at the main entrance and obtain a visitor's pass. For campus
                  tours, please contact our administrative office to schedule an
                  appointment.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-blue-900 text-white p-8 rounded-lg text-center" data-id="element-2822">
          <h2 className="text-2xl font-bold mb-4" data-id="element-2823">
            Experience Our Campus In Person
          </h2>
          <p className="mb-6 max-w-2xl mx-auto" data-id="element-2824">
            While our virtual tour gives you a glimpse of our facilities,
            nothing compares to experiencing the Isonyin Grammar School campus
            in person. Schedule a visit today to see our facilities, meet our
            staff, and get a feel for our vibrant learning community.
          </p>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-2825">
            <Button href="/contact" variant="primary" size="lg" data-id="element-2826">
              Schedule a Visit
            </Button>
            <Button href="/admissions" variant="outline" size="lg" className="border-white text-white hover:bg-white/10" data-id="element-2827">
              Apply for Admission
            </Button>
          </div>
        </section>
      </div>
    </div>;
};
export default Campus;